<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <MeuLinkConvite :codigo-indicacao="codigoIndicacao" />

        <div class="card">
          <!-- Card header -->
          <div class="pb-0 card-header">
            <div class="d-lg-flex">
              <div>
                <h5 class="mb-0">Rede Linear</h5>
                <p class="mb-0 text-sm">Seus indicados diretos e toda a downline agrupada por nível</p>
              </div>
              <div class="my-auto mt-4 ms-auto mt-lg-0">
                <div class="my-auto ms-auto">
                  <span v-if="!loadingAuth && !loadingDownline && membroId" class="badge bg-gradient-info text-sm">
                    <i class="fas fa-users me-1"></i>
                    {{ downline.length }} {{ downline.length === 1 ? 'pessoa na rede' : 'pessoas na rede' }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="card-body">
            <!-- Loading -->
            <div v-if="loadingAuth || loadingDownline" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Carregando...</span>
              </div>
              <p class="text-sm text-secondary mt-3 mb-0">Carregando sua rede...</p>
            </div>

            <!-- Erro de autenticação -->
            <div v-else-if="erroAuth" class="alert alert-warning text-sm" role="alert">
              <i class="fas fa-exclamation-triangle me-2"></i>
              <strong>Erro de autenticação:</strong> {{ erroAuth }}
            </div>

            <!-- Não sincronizado -->
            <div v-else-if="naoSincronizado" class="empty-state">
              <div class="empty-icon bg-gradient-warning">
                <i class="fas fa-sync-alt"></i>
              </div>
              <h6 class="mt-3">Você ainda não está na rede</h6>
              <p class="text-sm text-secondary mb-0">
                Peça ao gestor para sincronizar os treinadores do Laravel
              </p>
              <p class="text-xs text-secondary mt-1">
                <em>Rede de Marketing &rsaquo; Treinadores na Rede &rsaquo; Sincronizar</em>
              </p>
            </div>

            <!-- Vazio -->
            <div v-else-if="downline.length === 0" class="empty-state">
              <div class="empty-icon bg-gradient-primary">
                <i class="fas fa-user-plus"></i>
              </div>
              <h6 class="mt-3">Você ainda não tem indicados</h6>
              <p class="text-sm text-secondary mb-0">
                Compartilhe seu link de convite acima e comece a crescer sua rede
              </p>
            </div>

            <!-- Conteúdo -->
            <div v-else>
              <div v-for="grupo in downlinePorNivel" :key="grupo.nivel" class="mb-4">
                <div class="d-flex align-items-center mb-2">
                  <span class="badge badge-sm bg-gradient-primary me-2">Nível {{ grupo.nivel }}</span>
                  <span class="text-sm text-secondary">
                    {{ grupo.itens.length }} {{ grupo.itens.length === 1 ? 'pessoa' : 'pessoas' }}
                  </span>
                </div>

                <div class="table-responsive">
                  <table class="table table-hover table-sm align-items-center mb-0">
                    <thead>
                      <tr>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Nome</th>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">Código</th>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Patrocinador</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in grupo.itens" :key="item.membroId">
                        <td>
                          <div class="d-flex align-items-center">
                            <div class="avatar-sm bg-gradient-primary text-white rounded-circle me-2">
                              {{ iniciais(item.nomeMembro) }}
                            </div>
                            <span class="text-sm font-weight-bold">{{ item.nomeMembro }}</span>
                          </div>
                        </td>
                        <td class="text-center">
                          <span class="badge bg-gradient-light text-dark text-xs font-monospace">
                            {{ item.codigoIndicacao }}
                          </span>
                        </td>
                        <td class="text-sm text-secondary">{{ item.nomePatrocinador || '—' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import redeMarketingApi from '@/services/api/redeMarketingRepository'
import { useMembroLogado } from './useMembroLogado'
import MeuLinkConvite from './MeuLinkConvite.vue'

const { loading: loadingAuth, erro: erroAuth, membroId, codigoIndicacao, naoSincronizado, garantir } = useMembroLogado()

const loadingDownline = ref(false)
const downline = ref([])

const downlinePorNivel = computed(() => {
  const mapa = new Map()
  downline.value.forEach((d) => {
    if (!mapa.has(d.nivel)) mapa.set(d.nivel, [])
    mapa.get(d.nivel).push(d)
  })
  return Array.from(mapa.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([nivel, itens]) => ({ nivel, itens }))
})

function iniciais(nome) {
  if (!nome) return '?'
  const p = nome.trim().split(/\s+/)
  return (p.length === 1 ? p[0].substring(0, 2) : p[0][0] + p[p.length - 1][0]).toUpperCase()
}

async function carregarDownline() {
  if (!membroId.value) return
  loadingDownline.value = true
  try {
    const resp = await redeMarketingApi.Downline(membroId.value)
    downline.value = resp.data
  } catch (e) {
    console.warn('[RedeLinear] downline falhou:', e.message)
  } finally {
    loadingDownline.value = false
  }
}

watch(membroId, (v) => { if (v) carregarDownline() })

onMounted(async () => {
  await garantir()
  if (membroId.value) carregarDownline()
})
</script>

<style scoped>
.empty-state {
  text-align: center;
  padding: 40px 20px;
}
.empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.6rem;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
}
.avatar-sm {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 700;
  flex-shrink: 0;
}
</style>
